import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, Timestamp, query, orderBy, limit as limitQuery, updateDoc, doc, increment, where, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import { useAuth } from '../Auth/Auth';
import logger from '../../utils/logger';
import MovementForm from './MovementForm';
import MovementList from './MovementList';

const MovementView = () => {
  const [movements, setMovements] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { currentUser } = useAuth();

  // Fetch movements
  const fetchMovements = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'movements'), orderBy('date', 'desc'), limitQuery(500));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMovements(items);
    } catch (error) {
      logger.error('Error fetching movements:', error);
      toast.error('Failed to load movement history');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stock items for dropdown
  const fetchStockItems = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'stock'));
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setStockItems(items);
    } catch (error) {
      logger.error('Error fetching stock items:', error);
    }
  };

  useEffect(() => {
    fetchMovements();
    fetchStockItems();
  }, []);

  const handleAddNew = () => {
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      // Process each item in the submission
      const itemsToProcess = formData.items || [];
      let successCount = 0;
      let jobCardUpdated = false;

      for (const item of itemsToProcess) {
        // Find the stock item
        const stockItem = stockItems.find(s => s.id === item.stockItemId);

        if (!stockItem) {
          toast.error(`Stock item not found for ID: ${item.stockItemId}`);
          continue;
        }

        // Calculate new balance
        let newBalance;
        if (formData.movementType === 'IN') {
          newBalance = stockItem.quantity + item.quantity;
        } else {
          if (stockItem.quantity < item.quantity) {
            toast.error(`Insufficient stock for ${stockItem.itemName}: Available ${stockItem.quantity}, Requested ${item.quantity}`);
            continue;
          }
          newBalance = stockItem.quantity - item.quantity;
        }

        // Create movement record
        const movementData = {
          itemCode: stockItem.partNumber,
          itemDescription: stockItem.itemName,
          stockItemId: stockItem.id,
          movementType: formData.movementType,
          transactionType: formData.transactionType,
          quantityIn: formData.movementType === 'IN' ? item.quantity : 0,
          quantityOut: formData.movementType === 'OUT' ? item.quantity : 0,
          balanceAfter: newBalance,
          unitCost: stockItem.unitCost,
          totalValue: item.quantity * stockItem.unitCost,
          date: formData.date || Timestamp.now(),
          jobcardNo: formData.jobcardNo || '',
          vehicleRegNo: formData.vehicleRegNo || '',
          vehicleModel: formData.vehicleModel || '',
          customerName: formData.customerName || '',
          technician: formData.technician || '',
          supplier: formData.supplier || '',
          remarks: formData.remarks || '',
          recordedBy: currentUser?.displayName || currentUser?.email || 'System',
          location: stockItem.location,
        };

        // Add movement record
        await addDoc(collection(db, 'movements'), movementData);

        // Update stock quantity
        const stockRef = doc(db, 'stock', stockItem.id);
        if (formData.movementType === 'IN') {
          await updateDoc(stockRef, {
            quantity: increment(item.quantity),
            lastUpdated: Timestamp.now(),
          });
        } else {
          await updateDoc(stockRef, {
            quantity: increment(-item.quantity),
            lastUpdated: Timestamp.now(),
          });
        }

        successCount++;

        // Update job card tracker if this is an ALLOCATION to a job card (only once per submission)
        if (!jobCardUpdated &&
            formData.movementType === 'OUT' &&
            formData.transactionType === 'ALLOCATION' &&
            formData.jobcardNo) {
          try {
            // Find the job by job card number
            const jobQuery = query(
              collection(db, 'jobs'),
              where('jobCardNo', '==', formData.jobcardNo)
            );
            const jobSnapshot = await getDocs(jobQuery);

            if (!jobSnapshot.empty) {
              const jobDoc = jobSnapshot.docs[0];
              const jobRef = doc(db, 'jobs', jobDoc.id);

              // Update job tracker with "Parts Obtained" status
              await updateDoc(jobRef, {
                tracker: arrayUnion({
                  date: new Date(),
                  state: 'Parts Obtained',
                }),
              });
              jobCardUpdated = true;
            }
          } catch (error) {
            logger.error('Error updating job tracker:', error);
            // Don't fail the whole operation if tracker update fails
          }
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} movement${successCount > 1 ? 's' : ''} recorded successfully`);
        setShowForm(false);
        fetchMovements();
        fetchStockItems(); // Refresh stock items to get updated quantities
      } else {
        toast.error('No movements were recorded. Please check the errors above.');
      }
    } catch (error) {
      logger.error('Error recording movement:', error);
      toast.error('Failed to record movement');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-500 to-teal-600 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
              <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-black text-white">Movement History</h1>
              <p className="text-emerald-50 text-sm mt-1">
                Track all inventory inflows and outflows
              </p>
            </div>
          </div>
          <button
            onClick={handleAddNew}
            className="inline-flex items-center px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold text-emerald-700 bg-white hover:bg-emerald-50 transition-all transform hover:scale-105"
          >
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Record Movement
          </button>
        </div>

        {/* Movement Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" onClick={handleFormCancel}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white flex items-center">
                      <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Record New Movement
                    </h3>
                    <button onClick={handleFormCancel} className="text-white hover:text-emerald-100 transition-colors">
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="bg-white px-6 py-6">
                  <MovementForm
                    stockItems={stockItems}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Movement List */}
        <MovementList
          movements={movements}
          loading={loading}
          onRefresh={fetchMovements}
        />
      </div>
    </div>
  );
};

export default MovementView;
